import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SectionMessage } from '@/components/atoms/SectionMessage';
import { useUsd } from '@/hooks/useUsd';
import type { MetricRange } from '@/schemas/commons';
import { EMPTY_VALUE } from '@/utils/placeholders';

import { DEFAULT_RANGE } from './Costs.constants';
import { KpiCard, RangeToggle } from './Costs.Controls';
import { LayerBar } from './Costs.Layers';
import { CostsLoading } from './Costs.loading';
import { ModelTable } from './Costs.Models';
import { useCostsData } from './Costs.requests';
import { styles } from './Costs.styles';
import type { CostsBodyProps } from './Costs.types';
import { perConversation } from './Costs.utils';

const CostsBody = ({ metrics: m, prices }: CostsBodyProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'costs' });
	const usd = useUsd();
	const { measuredCost } = m;
	const perConv = perConversation(m.cost.value, m.activeThreads.value);

	return (
		<>
			<section className={styles.kpiGrid} aria-label={t('TITLE')}>
				<KpiCard label={t('KPI_SPEND_LABEL')} value={usd.total(m.cost.value)} sub={m.cost.deltaLabel} />
				<KpiCard label={t('KPI_MTD_LABEL')} value={m.cost.monthToDateFormatted} sub={t('KPI_MTD_SUB')} />
				<KpiCard
					label={t('KPI_PER_CONV_LABEL')}
					value={perConv == null ? EMPTY_VALUE : usd.unit(perConv)}
					sub={t('KPI_PER_CONV_SUB', { activeThreads: m.activeThreads.formatted })}
				/>
			</section>

			<section className={styles.sectionCard}>
				<h2 className={styles.sectionTitle}>{t('BY_LAYER_TITLE')}</h2>
				{measuredCost && measuredCost.byLayer.length > 0 ? (
					<LayerBar layers={measuredCost.byLayer} />
				) : (
					<SectionMessage>{t('NO_MEASURED_BODY')}</SectionMessage>
				)}
			</section>

			<section className={styles.sectionCard}>
				<h2 className={styles.sectionTitle}>{t('BY_MODEL_TITLE')}</h2>
				{measuredCost && measuredCost.byModel.length > 0 ? (
					<>
						<ModelTable models={measuredCost.byModel} prices={prices} />
						<p className={styles.tokenNote}>{t('TOKEN_NOTE')}</p>
					</>
				) : (
					<SectionMessage>{t('NO_MEASURED_BODY')}</SectionMessage>
				)}
			</section>
		</>
	);
};

export const Costs = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'costs' });
	const [range, setRange] = useState<MetricRange>(DEFAULT_RANGE);
	const { metrics, pricing, isLoading, isError } = useCostsData(range);

	if (isLoading) return <CostsLoading />;
	if (isError || !metrics.data || !pricing.data) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
				<RangeToggle value={range} onChange={setRange} />
			</header>

			<main className={styles.content}>
				<header>
					<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
					<h1 className={styles.pageH1}>{t('TITLE')}</h1>
					<p className={styles.pageSub}>{t('SUBTITLE')}</p>
				</header>

				<CostsBody metrics={metrics.data} prices={pricing.data.items} />
			</main>
		</>
	);
};
